import { computed, ref, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { getData, postData } from '@/api';

// Utility function to remove duplicates based on the 'url' field
const removeDuplicates = (favorites) => {
    const uniqueFavorites = [];
    const seen = new Set();
    for (const fav of favorites) {
        // Normalize 'to' to 'url' if necessary
        const normalizedUrl = fav.url || fav.to;
        if (!seen.has(normalizedUrl)) {
            seen.add(normalizedUrl);
            uniqueFavorites.push({
                ...fav,
                url: normalizedUrl,  // Always use 'url' as the final field
            });
        }
    }
    return uniqueFavorites;
};

export function useFavorites(user) {
    const store = useStore();
    const favorites = computed(() => store.state.auth.preferences.favorites);
    let saveTimer;
    let previousFavorites = [];

    // Update previousFavorites whenever favorites change
    watchEffect(() => {
        if (store?.state?.auth?.preferences?.favorites) {
            previousFavorites = [...store.state.auth.preferences.favorites];
        }
    });

    /**
     * Load favorites from the API and ensure no duplicates
     */
    const loadFavorites = async () => {
        if (!user?.id) return; // Avoid API call if user ID is invalid

        const userId = Number(user.id);
        try {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
            const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/get-favorites`;
            const data = await getData(apiUrl);        

            if (data && data.favorites) {
                // Remove duplicates before updating the store
                const uniqueFavorites = removeDuplicates(data.favorites);
                store.dispatch('auth/setFavorites', uniqueFavorites);
                previousFavorites = [...uniqueFavorites];
            } else {
                store.dispatch('auth/setFavorites', []);
            }
        } catch (error) {
            console.error('Error loading favorites from API:', error);
            store.dispatch('auth/setFavorites', []);
        }
    };

    /**
     * Schedule an API update with debounce
     */
    const saveFavorites = () => {
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => sendFavoritesToApi(), 3000); // Save after 3 seconds
    };
    /**
     * Add a new favorite and ensure reactivity
     * @param {Object} favorite - The favorite item to add
     * @param {string} favorite.url - The URL of the favorite
     *  
     * @param {string} favorite.to - The route to navigate to (optional)
     * @param {string} favorite.name - The name of the favorite
     * 
     * **/
    const addFavorite = (favorite) => {
        const favoriteUrl = favorite.url || favorite.to;
        if (!favoriteUrl) {
            console.error('Favorite must have a "url" or "to" property');
            return;
        }
    
        const newFavorite = {
            ...favorite,
            url: favoriteUrl,
            user_id: user.id,
        };
    
        // Get existing favorites and deduplicate with the new one
        const current = store.state.auth.preferences.favorites || [];
        const updatedFavorites = removeDuplicates([...current, newFavorite]);
    
        store.dispatch('auth/setFavorites', updatedFavorites); // ✅ Only set once
        saveFavorites();
    };
    

    /**
     * Remove an item from favorites and ensure reactivity
     */
    const removeFavorite = (url) => {
    
        if (!store) return;

        // Dispatch the removal action (make sure it actually removes the favorite)
        store.dispatch('auth/removeFavorite', url);

        // Update Vuex store directly after removal
        const updatedFavorites = removeDuplicates(store.state.auth.preferences.favorites);
        store.dispatch('auth/setFavorites', updatedFavorites);

        saveFavorites(); // Save after removing
    };

    /**
     * Compare and send updates if needed
     */
    const compareAndSendUpdates = () => {
        const addedFavorites = favorites.value.filter(fav => 
            !previousFavorites.some(prevFav => prevFav.url === fav.url)
        );
        const removedFavorites = previousFavorites.filter(prevFav => 
            !favorites.value.some(fav => fav.url === prevFav.url)
        );

        if (addedFavorites.length || removedFavorites.length) {
            sendFavoritesToApi();
        }
    };

    const sendFavoritesToApi = async () => {
        if (!store || !user?.id) return;

        try {
            if (!store.state.auth.isAuthenticated) {
                return; // Prevent saving if the user is not authenticated
            }

            // Deduplicate favorites before sending
            const uniqueFavorites = removeDuplicates(favorites.value);

            const data = {
                user_id: Number(user.id),
                favorites: uniqueFavorites,
            };

            await postData('/api/save-favorites', data);
            previousFavorites = [...uniqueFavorites]; // Update previousFavorites to avoid unnecessary API calls
        } catch (error) {
            console.error('Error sending favorites to API:', error);
        }
    };

    /**
     * Clear all favorites
     */
    const clearFavorites = () => {
        if (!store) return;

        store.dispatch('auth/clearFavorites');
    };

    return {
        favorites,
        addFavorite,
        removeFavorite,
        loadFavorites,
        saveFavorites,
        compareAndSendUpdates,
        clearFavorites,
    };
}
